/**
 * ComplexityController — the gameplay component that drives FlowBot.
 *
 * Composes four collaborators, each independently testable:
 *   - `complexityModel`   — the pure oracle recurrence (verdicts → band).
 *   - `AssessmentStream`  — the indexed assessment sequence; owns PAD threading.
 *   - `flowRegeneration`  — rest-phrase policy (stuck + accuracy → inject?).
 *   - `complexityMachine` — lifecycle (idle / active / restPending).
 *
 * The controller itself holds almost no state: it wires those four together and
 * exposes a small imperative surface for the scene. Everything numeric lives in
 * the stream or is recomputed from it, so there is no second source of truth to
 * drift.
 *
 * Instantiate behind `FeatureFlag.USE_COMPLEXITY_ADJUSTMENT` — when the flag is
 * off the controller simply is not constructed, and no complexity behaviour can
 * reach the scene.
 *
 * ## On PAD
 *
 * PAD is NOT applied here. The model already encodes it: the band for
 * assessment phrase `p` is driven by `verdict[p - pad]`. The render pipeline
 * supplies exactly that delay for free — `PhraseManager` requests the next
 * phrase at phrase end and consumes it at measure 2 of the following phrase, so
 * a phrase requested at the end of `p` first plays as `p + pad`. Deferring the
 * verdict again inside the controller would double-apply the delay.
 * {@link planNextPhrase} asserts the invariant.
 *
 * That free delay is only correct if the finished phrase is GRADED BEFORE the
 * next request is planned — both happen on the same phrase-end event, and the
 * order between them is the difference between an effective delay of `pad` and
 * one of `pad + 1`. `PhraseManager.createCursor`'s `onFinishPlaying` owns that
 * ordering; the shortfall branch in {@link planNextPhrase} is what catches it
 * if the seam ever moves again.
 */

import { bandFor, classify, computeTrajectory } from './complexityModel';
import {
  AccuracySource,
  AssessmentBand,
  Band,
  DEFAULT_TIER_CONFIG,
  RATE_RANGE,
  TierConfig,
  Verdict,
  isRateInRange,
} from './complexityModel.types';
import { AssessmentStream, PhraseKind } from './AssessmentStream';
import { GradedNote, tallyBreakdown } from './errorTally';
import { restBand, shouldInjectRest } from './flowRegeneration';
import { ComplexityTelemetry } from './ComplexityTelemetry';
import { NwcsBand, SessionComplexityMap, TierComplexityScore } from './SessionComplexityMap';
import { complexityMachine } from './complexityMachine';
import type { ComplexityPhase } from './complexityMachine.types';
import { createActor, type ActorRefFrom } from 'xstate';

/** What the request path should fetch next. */
export interface PhrasePlan {
  /** Assessment or rest. Rest phrases consume no assessment index. */
  kind: PhraseKind;
  /** Complexity band to request, in normalized [0, 1]. */
  crMin: number;
  crMax: number;
  /** Assessment index this phrase will take, or `undefined` for a rest phrase. */
  idx?: number;
  /**
   * The same band denormalized to raw NWCS for the backend, or `undefined` when
   * the session map is not loaded — in which case the request carries no
   * complexity filter and the backend selects as it did pre-feature.
   */
  requestBand?: NwcsBand;
}

/** Emitted whenever a graded phrase moves (or holds) the band. */
export interface BandChange {
  /** Band now in effect for the next assessment phrase. */
  band: AssessmentBand;
  /** The verdict that caused this evaluation. */
  verdict: Verdict;
  /** Complexity error tally behind the verdict. */
  errorTotal: number;
  /** Assessment index that was just graded. */
  gradedIdx: number;
}

export interface ComplexityControllerConfig {
  /** Per-tier tunables. Defaults to the oracle's active preset. */
  tierConfig?: TierConfig;
  /** Live accuracy provider. See the WMA note on {@link AccuracySource}. */
  accuracySource: AccuracySource;
  /** Fired after each graded phrase, with the band now in effect. */
  onBandChanged?: (change: BandChange) => void;
  /** Fired when a rest phrase is planned, with the band it is drawn from. */
  onRestInjected?: (band: Readonly<Band>) => void;
  /** Diagnostics sink. Defaults to a no-op so tests stay quiet. */
  logger?: (message: string) => void;
}

/**
 * Monotonic tag for controller instances, used only in the log prefix.
 *
 * The scene rebuilds the controller on every `init()` — tier change, but also
 * any scene restart — and a rebuilt controller starts a fresh assessment stream
 * at idx 1. Since a plan always registers and always advances the index, the
 * SAME `idx=N` line can never be logged twice by one instance: a repeat means a
 * new session (different tag) or a plan that failed to register (same tag,
 * preceded by the throw from `AssessmentStream.register`). The tag is what tells
 * those two apart in a log capture.
 */
let controllerInstanceCount = 0;

export default class ComplexityController {
  private readonly instanceId = ++controllerInstanceCount;
  private readonly config: TierConfig;
  private readonly accuracySource: AccuracySource;
  private readonly onBandChanged?: (change: BandChange) => void;
  private readonly onRestInjected?: (band: Readonly<Band>) => void;
  private readonly log: (message: string) => void;
  // Arrow, not `this.log` directly: field initializers run before the
  // constructor body, so `this.log` is not assigned yet at this point.
  private readonly stream = new AssessmentStream((message) =>
    this.log(`AssessmentStream: ${message}`),
  );
  private readonly actor: ActorRefFrom<typeof complexityMachine>;
  private readonly sessionMap = new SessionComplexityMap();
  private readonly telemetry = new ComplexityTelemetry();

  constructor({
    tierConfig = DEFAULT_TIER_CONFIG,
    accuracySource,
    onBandChanged,
    onRestInjected,
    logger,
  }: ComplexityControllerConfig) {
    this.config = tierConfig;
    this.accuracySource = accuracySource;
    this.onBandChanged = onBandChanged;
    this.onRestInjected = onRestInjected;
    const sink = logger ?? (() => undefined);
    this.log = (message) => sink(`[FlowBot#${this.instanceId}] ${message}`);
    // Report rather than reject: a per-tier config outside the Climb/Drop Rate
    // bounds still produces a valid trajectory, just one no user could have
    // selected, so the useful outcome is a visible signal rather than a dead
    // session. Both rates are checked; either can be set independently.
    (['cai', 'cdi'] as const).forEach((key) => {
      if (!isRateInRange(this.config[key])) {
        this.log(
          `${key}=${this.config[key]} is outside the Climb/Drop Rate range ` +
            `[${RATE_RANGE.min}, ${RATE_RANGE.max}] — running with it anyway`,
        );
      }
    });
    this.actor = createActor(complexityMachine);
    this.actor.start();
  }

  /** Begin a session. Until this is called nothing may be planned or graded. */
  startSession(): void {
    this.actor.send({ type: 'SESSION_STARTED' });
    this.log(`session started, seed floor=${this.config.complexityStart}`);
  }

  /**
   * Load the tier's raw NWCS scores so planned bands can be denormalized for the
   * backend. Until this is called, plans carry no `requestBand` and phrase
   * selection is unaffected — the controller still tracks the band internally.
   *
   * Safe to call more than once (e.g. a mid-session tier change); the newest
   * scores replace the old.
   */
  loadSessionMap(scores: readonly TierComplexityScore[]): void {
    this.sessionMap.load(scores);
    this.log(
      `session map ${this.sessionMap.isLoaded() ? `loaded (${this.sessionMap.size()} scored phrases)` : 'not usable — complexity filtering disabled'}`,
    );
  }

  /** True once a usable session map has been loaded. */
  hasSessionMap(): boolean {
    return this.sessionMap.isLoaded();
  }

  /**
   * Decide what to fetch next, and register it in the assessment stream.
   *
   * Call this once per phrase request, at the point the scene asks for a new
   * phrase. The returned plan's `requestBand` is what the request path should
   * filter on (raw NWCS, or undefined when the session map is not loaded).
   *
   * @param phraseKey  Stable id for this request — the render pipeline's
   *                   `sequenceId`. The same key must be passed to
   *                   {@link ComplexityController.onPhraseEnd} when the phrase
   *                   finishes, so plan and grade line up regardless of any
   *                   phrase-uuid reuse from repeat-avoidance.
   * @throws if the session has not started.
   */
  planNextPhrase(phraseKey: string): PhrasePlan {
    if (this.getPhase() === 'idle') {
      throw new Error('ComplexityController: planNextPhrase before startSession');
    }

    const bands = this.getTrajectory();
    const wantsRest =
      this.getPhase() === 'active' &&
      shouldInjectRest(
        {
          bands,
          accuracy: this.accuracySource.currentAccuracy(),
          assessmentsSinceLastRest: this.stream.getAssessmentsSinceLastRest(),
        },
        this.config,
      );

    if (wantsRest) {
      const currentPosition =
        bands.length > 0 ? bands[bands.length - 1].position : this.config.complexityStart;
      const band = restBand(currentPosition, this.config);
      this.stream.register(phraseKey, PhraseKind.Rest, band);
      this.actor.send({ type: 'REST_INJECTED' });
      this.onRestInjected?.(band);
      this.log(`rest phrase injected at ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)}`);
      return {
        kind: PhraseKind.Rest,
        crMin: band.crMin,
        crMax: band.crMax,
        requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
      };
    }

    const idx = this.stream.getNextIdx();
    const band = bandFor(idx, this.stream.getVerdicts(), this.config);

    // PAD invariant: the band for phrase `idx` is driven by `verdict[idx - pad]`,
    // so exactly `idx - pad` phrases should be graded when we plan. The two
    // directions are NOT symmetric:
    //
    // - A SHORTFALL is the bug. The driver has not been recorded yet, so
    //   `bandFor` finds no verdict at `idx - pad`, falls through to the hold
    //   branch, and the phrase is fetched from a band that is one verdict stale
    //   — an effective delay of pad + 1 against a model that assumes pad. It
    //   means the request is being planned before the finished phrase is graded;
    //   see the ordering note in this file's header.
    // - A SURPLUS is benign. We are planning later than the bare request/consume
    //   pipeline would, which is exactly what an injected rest phrase does: it
    //   takes a render slot without taking an assessment index. `verdict[idx -
    //   pad]` is still the driver and the band is unchanged; there is simply a
    //   verdict on hand that this row does not use yet.
    //
    // Seeds are exempt by construction: `expected` is 0 for idx <= pad, and
    // nothing is graded before the first phrase ends.
    const graded = this.stream.getGradedCount();
    const expected = Math.max(0, idx - this.config.pad);
    if (graded < expected) {
      this.log(
        `PAD invariant VIOLATED: planning idx=${idx} with ${graded} graded, expected ${expected} — ` +
          `driver verdict[${idx - this.config.pad}] is not recorded yet, so this phrase is drawn ` +
          `one verdict stale (effective delay ${this.config.pad + (expected - graded)})`,
      );
    } else if (graded > expected) {
      this.log(
        `planning idx=${idx} with ${graded} graded, expected ${expected} — behind the pipeline ` +
          `(rest-phrase interleave); band unaffected`,
      );
    }

    // Narrowed to the band alone: `bandFor` also returns idx/driver/cooldown,
    // which are trajectory bookkeeping and have no meaning once replayed back
    // per phrase.
    this.stream.register(phraseKey, PhraseKind.Assessment, {
      position: band.position,
      crMin: band.crMin,
      crMax: band.crMax,
    });
    this.telemetry.recordPlan({
      idx,
      position: band.position,
      crMin: band.crMin,
      crMax: band.crMax,
      driver: band.driver,
      cooldown: band.cooldown,
      restsBefore: this.getRestCount(),
    });
    return {
      kind: PhraseKind.Assessment,
      crMin: band.crMin,
      crMax: band.crMax,
      idx,
      requestBand: this.sessionMap.denormalizeBand(band.crMin, band.crMax),
    };
  }

  /**
   * Grade a phrase that has finished playing.
   *
   * Rest phrases are recognised and deliberately skipped — they receive no
   * verdict, do not move the band, and do not consume a PAD slot.
   *
   * @param phraseKey the same key passed to {@link planNextPhrase} for this
   *              phrase (the render pipeline's `sequenceId`).
   * @param notes the phrase's graded notes, read from `EventStream`. The tally
   *              uses complexity weights (red 1.0 / yellow 0.5), not
   *              `EventStream`'s accuracy scale.
   */
  onPhraseEnd(phraseKey: string, notes: readonly GradedNote[]): void {
    const entry = this.stream.getEntry(phraseKey);
    if (entry === undefined) {
      this.log(`ignoring unknown phrase ${phraseKey}`);
      return;
    }

    if (entry.kind === PhraseKind.Rest) {
      this.actor.send({ type: 'REST_COMPLETED' });
      this.log(`rest phrase complete — band untouched`);
      return;
    }

    // A phrase can finish playing more than once — the cursor re-enters the same
    // phrase on rollback / resume, and each re-entry fires onFinishPlayingPhrase
    // again (the phrase-request path dedupes by index and ATA by uuid for the
    // same reason). One assessment phrase is one verdict, graded on first
    // completion, so ignore any later finish rather than double-grading.
    if (entry.verdict !== undefined) {
      this.log(`phrase ${phraseKey} already graded — ignoring duplicate finish`);
      return;
    }

    const { reds, yellows, errorTotal } = tallyBreakdown(notes);
    const verdict = classify(errorTotal, this.config);
    this.stream.grade(phraseKey, verdict, errorTotal);

    const gradedIdx = entry.idx as number;
    this.telemetry.recordGrade(gradedIdx, {
      verdict,
      errorTotal,
      redNotes: reds,
      yellowNotes: yellows,
      accuracy: this.accuracySource.currentAccuracy(),
    });
    const band = bandFor(gradedIdx + this.config.pad, this.stream.getVerdicts(), this.config);
    this.log(
      `idx=${gradedIdx} errors=${errorTotal} (${reds}R ${yellows}Y) verdict=${verdict} ` +
        `-> band ${band.crMin.toFixed(2)}-${band.crMax.toFixed(2)} (applies at idx=${band.idx})`,
    );
    this.onBandChanged?.({ band, verdict, errorTotal, gradedIdx });
  }

  /** The full band trajectory so far — telemetry, tests, and the stuck check. */
  getTrajectory(): AssessmentBand[] {
    const graded = this.stream.getGradedCount();
    if (graded === 0) return [];
    return computeTrajectory(this.stream.getVerdicts(), this.config, graded);
  }

  /** Band that will apply to a given assessment index. */
  getBand(idx: number): AssessmentBand {
    return bandFor(idx, this.stream.getVerdicts(), this.config);
  }

  /**
   * The band a specific planned phrase was drawn from, keyed by the render
   * pipeline's `sequenceId`.
   *
   * This is the band as it was at plan time — deliberately not a recomputation,
   * because the trajectory moves on as later phrases are graded and the caller
   * wants what the phrase *now playing* was actually drawn from. Rest phrases
   * return their (lowered) rest band; unknown keys return `undefined`.
   */
  getPlannedBand(phraseKey: string): Readonly<Band> | undefined {
    return this.stream.getEntry(phraseKey)?.band;
  }

  /**
   * The band the session opens on, before any phrase has been planned. Used to
   * seed the header progress bar so it does not start empty.
   */
  getSeedBand(): AssessmentBand {
    return bandFor(1, [], this.config);
  }

  getPhase(): ComplexityPhase {
    return this.actor.getSnapshot().value as ComplexityPhase;
  }

  getRestCount(): number {
    return this.actor.getSnapshot().context.restsInjected;
  }

  /**
   * Whether the phrase registered under `phraseKey` is a rest phrase. Used by
   * the scene at play time (keyed by the render pipeline's sequenceId) to drive
   * the rest-phrase UI. Unknown keys are not rest phrases.
   */
  isRest(phraseKey: string): boolean {
    return this.stream.isRest(phraseKey);
  }

  /** Read-only view of the assessment stream, for telemetry and tests. */
  getStream(): AssessmentStream {
    return this.stream;
  }

  getTierConfig(): TierConfig {
    return this.config;
  }

  /**
   * Clear all state. The scene instance is reused across tier changes and class
   * field initialisers do not re-run, so this must be called on tier change.
   */
  /** Per-phrase telemetry recorder — for oracle comparison. See
   *  {@link ComplexityTelemetry} and `installFlowbotConsole`. */
  getTelemetry(): ComplexityTelemetry {
    return this.telemetry;
  }

  reset(): void {
    this.stream.reset();
    this.sessionMap.load([]);
    this.telemetry.reset();
    this.actor.send({ type: 'RESET' });
    this.log('reset');
  }

  destroy(): void {
    this.actor.stop();
  }
}
